import _extends from "@babel/runtime/helpers/esm/extends";
import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
var _excluded = ["id", "title", "message", "closeable", "hyperLink", "status", "className", "icon", "closeIcon", "onClose", "close", "clickToClose", "showTimer", "position", "duration", "children", "onClick", "expandable", "config", "showNotice"];
import * as React from "react";
import { useCallback } from 'react';
import classNames from "classnames";
import { useTimeout } from "./useTimeout";
import { KEY_VALUES } from '../utils';
var statuses = {
  'success': 'icon-success-fill',
  'warning': 'icon-warning-fill',
  'error': 'icon-error-fill'
};
var Notification = /*#__PURE__*/React.forwardRef(function (props, ref) {
  var _classNames, _navigator$userAgent, _navigator$userAgent2;
  var id = props.id,
    title = props.title,
    message = props.message,
    _props$closeable = props.closeable,
    closeable = _props$closeable === void 0 ? true : _props$closeable,
    hyperLink = props.hyperLink,
    _props$status = props.status,
    status = _props$status === void 0 ? 'default' : _props$status,
    className = props.className,
    customIcon = props.icon,
    customCloseIcon = props.closeIcon,
    onClose = props.onClose,
    close = props.close,
    _props$clickToClose = props.clickToClose,
    clickToClose = _props$clickToClose === void 0 ? false : _props$clickToClose,
    showTimer = props.showTimer,
    _props$position = props.position,
    position = _props$position === void 0 ? "static" : _props$position,
    _props$duration = props.duration,
    duration = _props$duration === void 0 ? 0 : _props$duration,
    children = props.children,
    onClick = props.onClick,
    _props$expandable = props.expandable,
    expandable = _props$expandable === void 0 ? false : _props$expandable,
    _props$config = props.config,
    config = _props$config === void 0 ? false : _props$config,
    _props$showNotice = props.showNotice,
    showNotice = _props$showNotice === void 0 ? false : _props$showNotice,
    restProps = _objectWithoutPropertiesLoose(props, _excluded);
  var circleRef = React.useRef();
  var _React$useState = React.useState(false),
    keyboardFocus = _React$useState[0],
    setKeyboardFocus = _React$useState[1];
  var circlePos;
  var curtime;
  duration > 0 && useTimeout(function () {
    close === null || close === void 0 ? void 0 : close();
    onClose === null || onClose === void 0 ? void 0 : onClose();
  }, duration);
  var isTopHeader = position === 'topHeader' || position === 'static-topHeader';
  var _React$useState2 = React.useState(false),
    expand = _React$useState2[0],
    setExpand = _React$useState2[1];
  React.useEffect(function () {
    var dismiss = duration;
    var l = Math.ceil(6 * 2 * 3.14);
    var timelen = Math.floor(dismiss / l / 10);
    circlePos = l * 2;
    var circleLength = circlePos,
      start = new Date().getTime();
    // Used for appkit4 site static display, don't pass 'static-topHeader' for producton usage
    if (position === 'static-topHeader') {
      circlePos = 62;
      if (!circleRef.current) {
        return;
      }
      circleRef.current.style.strokeDashoffset = circlePos;
    } else if (position === 'topHeader' && showTimer) {
      curtime = setInterval(function () {
        var current = new Date().getTime();
        circlePos = circleLength - Math.ceil((current - start) / timelen) * 0.1;
        if (circlePos <= l) {
          circlePos = l;
          clearInterval(curtime);
          return;
        }
        if (!circleRef.current) {
          return;
        }
        circleRef.current.style.strokeDashoffset = circlePos;
      }, timelen);
    }
  }, []);
  var handleClick = function handleClick(event) {
    if (clickToClose) {
      setKeyboardFocus(false);
      close === null || close === void 0 ? void 0 : close();
    }
    onClick === null || onClick === void 0 ? void 0 : onClick(props);
  };
  var onKeyDown = useCallback(function (event) {
    if (event.key === KEY_VALUES.ENTER || event.key === KEY_VALUES.SPACE) {
      event.preventDefault();
      if (config) {
        close === null || close === void 0 ? void 0 : close();
      }
      onClose === null || onClose === void 0 ? void 0 : onClose(event);
    }
  }, [close, onClose, config]);
  var onKeyUp = function onKeyUp(event) {
    if (event.key === KEY_VALUES.TAB) {
      setKeyboardFocus(true);
    }
  };
  var handleClose = useCallback(function (event) {
    if (config) {
      close === null || close === void 0 ? void 0 : close();
    }
    onClose === null || onClose === void 0 ? void 0 : onClose(event);
  }, [close, onClose, config]);
  var onExpand = function onExpand(event) {
    event.preventDefault();
    event.stopPropagation();
    setExpand(!expand);
  };
  var cx = statuses[status] || status;
  var classes = classNames('ap-notification', (_classNames = {}, _classNames[status] = isTopHeader, _classNames['ap-notice'] = showNotice, _classNames['has-icon'] = customIcon || cx, _classNames), className);
  var classeexpandable = classNames('Appkit4-icon icon-down-chevron-outline', {
    'roate': expand
  });
  var dropdownClasses = classNames('ap-notification-content-dialog', {
    'open': isTopHeader && expand
  });
  var node = /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", _extends({
    id: id,
    className: classes,
    tabIndex: isTopHeader ? -1 : 0,
    "aria-label": status + " notification: " + (typeof message == 'object' ? '' : message),
    ref: ref,
    onClick: handleClick
  }, restProps), /*#__PURE__*/React.createElement("div", {
    className: "ap-notification-content"
  }, (cx || customIcon) && (customIcon ? customIcon : /*#__PURE__*/React.createElement("span", {
    className: classNames("ap-notification-icon Appkit4-icon height", cx),
    "aria-hidden": "true"
  })), /*#__PURE__*/React.createElement("div", null, title && /*#__PURE__*/React.createElement("div", {
    className: "ap-notification-message height ap-font-medium"
  }, title), message && /*#__PURE__*/React.createElement("div", {
    tabIndex: typeof message == 'object' && !isTopHeader ? 0 : -1,
    className: "ap-notification-description"
  }, message), hyperLink), isTopHeader && expandable && /*#__PURE__*/React.createElement("div", {
    className: "ap-notification-toggle toggle-icon",
    onClick: onExpand
  }, /*#__PURE__*/React.createElement("span", {
    className: classeexpandable
  }))), isTopHeader && showTimer ? /*#__PURE__*/React.createElement("div", {
    role: "button",
    "aria-label": "Timer",
    className: "a-countdown-wrapper"
  }, /*#__PURE__*/React.createElement("svg", {
    height: "24",
    width: "24"
  }, /*#__PURE__*/React.createElement("circle", {
    className: "a-circle a-countdown animated",
    ref: circleRef,
    cx: "12",
    cy: "12",
    r: "6"
  }))) : closeable && /*#__PURE__*/React.createElement("button", {
    className: classNames("ap-notification-icon-close", {
      "keyboard-focus": keyboardFocus && ((_navigator$userAgent = navigator.userAgent) === null || _navigator$userAgent === void 0 ? void 0 : _navigator$userAgent.indexOf('Safari')) > -1 && ((_navigator$userAgent2 = navigator.userAgent) === null || _navigator$userAgent2 === void 0 ? void 0 : _navigator$userAgent2.indexOf('Chrome')) == -1
    }),
    "aria-label": "close",
    onClick: handleClose,
    onKeyDown: onKeyDown,
    onKeyUp: onKeyUp
  }, customCloseIcon ? customCloseIcon : /*#__PURE__*/React.createElement("span", {
    className: "Appkit4-icon icon-close-outline height ap-font-medium"
  }))), expandable && /*#__PURE__*/React.createElement("div", {
    "aria-hidden": expand ? false : true,
    tabIndex: expand ? 0 : -1,
    className: dropdownClasses
  }, /*#__PURE__*/React.createElement("div", {
    className: "ap-padding-box"
  }, children)));
  if (config) {
    return node;
  } else {
    return /*#__PURE__*/React.createElement("div", {
      className: "ap-notifications ap-notifications-" + position
    }, node);
  }
});
export default Notification;